/**
 * Eén foutvertaallaag voor app en portaal: ruwe (vaak Engelse) Supabase-, Edge Function- en netwerkfouten
 * → een Nederlandse melding in gewone taal + een concrete volgende stap. De ruwe tekst blijft beschikbaar
 * als "technische details" voor support, maar wordt nooit als hoofdtekst getoond.
 *
 * Toon (brandbook): kort, direct, je-vorm, zeg wat er gebeurde en wat je nu doet. Geen excuses, geen jargon.
 */

export type ErrorKind =
  | 'not_invited'
  | 'rate_limit'
  | 'otp_invalid'
  | 'credentials'
  | 'session'
  | 'network'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'invalid_input'
  | 'server'
  | 'lock'
  | 'cancelled'
  | 'unknown';

export interface FriendlyError {
  kind: ErrorKind;
  /** Wat er gebeurde, in één zin. */
  title: string;
  /** Wat je nu doet. */
  hint: string;
  /** Ruwe technische informatie voor support (bron, status, code, melding, body). */
  details: string;
  /** Opnieuw proberen heeft zin. */
  retryable: boolean;
  /** Genormaliseerde code (Supabase error code, Edge-body, Tuya-code, ...), voor tests/logging. */
  code?: string;
}

/** Genormaliseerde ruwe fout. */
export interface ErrorInfo {
  message: string;
  /** Bijv. 'issue-tuya-login', 'auth', 'register_paired_lock'. */
  source?: string;
  status?: number;
  code?: string;
  /** `error`/`message` uit een JSON-body (Edge Function). */
  bodyError?: string;
  name?: string;
}

/** Fout die zijn genormaliseerde info meedraagt (wrappers rond supabase-js gooien deze). */
export class AppError extends Error {
  constructor(public readonly info: ErrorInfo) {
    super(info.source ? `${info.source}: ${info.bodyError ?? info.message}` : (info.bodyError ?? info.message));
    this.name = 'AppError';
  }
}

type Loose = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** Synchrone extractie (geen body lezen). */
export function errorInfoSync(err: unknown, source?: string): ErrorInfo {
  if (err instanceof AppError) return { ...err.info, source: err.info.source ?? source };
  if (err && typeof err === 'object' && 'info' in err && (err as Loose).info && typeof (err as Loose).info === 'object') {
    const i = (err as { info: ErrorInfo }).info;
    if (typeof i.message === 'string') return { ...i, source: i.source ?? source };
  }
  if (typeof err === 'string') return { message: err, source };
  if (err && typeof err === 'object') {
    const e = err as Loose;
    const ctx = e.context as Loose | undefined;
    return {
      message: str(e.message) ?? String(err),
      source,
      name: str(e.name),
      code: str(e.code) ?? (typeof e.code === 'number' ? String(e.code) : undefined),
      status: num(e.status) ?? (ctx ? num(ctx.status) : undefined),
      bodyError: str(e.bodyError),
    };
  }
  return { message: String(err ?? 'Onbekende fout'), source };
}

/**
 * Lees ook de JSON-body van een Edge Function-fout (supabase-js FunctionsHttpError: `error.context` is de Response).
 * Gebruik dit in API-wrappers vóórdat de fout wordt doorgegeven, zodat de body niet verloren gaat.
 */
export async function errorInfo(err: unknown, source?: string): Promise<ErrorInfo> {
  const info = errorInfoSync(err, source);
  const ctx = err && typeof err === 'object' ? ((err as Loose).context as Loose | undefined) : undefined;
  if (ctx && !info.bodyError) {
    try {
      const res = (typeof ctx.clone === 'function' ? (ctx.clone as () => Loose)() : ctx) as Loose;
      let body: unknown;
      if (typeof res.json === 'function') body = await (res.json as () => Promise<unknown>)();
      else if (typeof res.text === 'function') body = JSON.parse(await (res.text as () => Promise<string>)());
      if (body && typeof body === 'object') {
        const b = body as Loose;
        info.bodyError = str(b.error) ?? str(b.message) ?? str(b.msg);
        info.code = info.code ?? str(b.code) ?? str(b.error_code);
      } else if (typeof body === 'string') info.bodyError = body;
    } catch {
      /* geen JSON-body */
    }
  }
  return info;
}

/** Fout omzetten naar AppError mét body (voor wrappers). */
export async function toAppError(err: unknown, source?: string): Promise<AppError> {
  return new AppError(await errorInfo(err, source));
}

export function formatDetails(i: ErrorInfo): string {
  const parts = [
    i.source && `Bron: ${i.source}`,
    i.status !== undefined && `Status: ${i.status}`,
    i.code && `Code: ${i.code}`,
    i.name && i.name !== 'Error' && `Type: ${i.name}`,
    `Melding: ${i.message}`,
    i.bodyError && i.bodyError !== i.message && `Server: ${i.bodyError}`,
    `Tijd: ${new Date().toISOString()}`,
  ];
  return parts.filter(Boolean).join('\n');
}

const make = (kind: ErrorKind, title: string, hint: string, retryable: boolean, i: ErrorInfo, code?: string): FriendlyError => ({
  kind,
  title,
  hint,
  retryable,
  details: formatDetails(i),
  code: code ?? i.code ?? i.bodyError,
});

/** Herken veelvoorkomende Supabase/Edge/netwerk-fouten. `null` = niet herkend. */
export function mapCommonError(i: ErrorInfo): FriendlyError | null {
  const code = (i.code ?? '').toLowerCase();
  const msg = `${i.message} ${i.bodyError ?? ''}`.toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => msg.includes(n));

  // --- Inloggen / uitnodiging ---
  if (code === 'signup_disabled' || code === 'otp_disabled' || code === 'user_not_found' || has('signups not allowed', 'user not found')) {
    return make(
      'not_invited',
      'Dit e-mailadres is nog niet uitgenodigd of geactiveerd.',
      'Vraag je clubbeheerder om een uitnodiging. Gebruik daarna precies het e-mailadres uit die uitnodiging.',
      false,
      i,
      code || 'signup_disabled',
    );
  }
  if (
    code.startsWith('over_') ||
    code === 'too_many_requests' ||
    i.status === 429 ||
    has('rate limit', 'too many requests', 'te snel opnieuw', 'for security purposes, you can only request')
  ) {
    return make('rate_limit', 'Je hebt dit te vaak achter elkaar geprobeerd.', 'Wacht een minuut en probeer het daarna opnieuw.', true, i, code || 'rate_limit');
  }
  if (code === 'otp_expired' || code === 'mfa_verification_failed' || code === 'mfa_challenge_expired' || has('token has expired or is invalid', 'invalid otp', 'otp has expired', 'invalid totp')) {
    return make('otp_invalid', 'Deze code klopt niet of is verlopen.', 'Controleer de 6 cijfers, of vraag een nieuwe code aan.', true, i, code || 'otp_expired');
  }
  if (code === 'invalid_credentials' || has('invalid login credentials')) {
    return make('credentials', 'E-mailadres of wachtwoord klopt niet.', 'Controleer je gegevens, of log in met een inloglink per e-mail.', true, i, 'invalid_credentials');
  }
  if (code === 'email_not_confirmed' || has('email not confirmed')) {
    return make('not_invited', 'Je e-mailadres is nog niet bevestigd.', 'Open eerst de uitnodiging of bevestigingsmail in je inbox.', false, i, 'email_not_confirmed');
  }

  // --- Netwerk (vóór status-checks: geen status bij een fetch-fout) ---
  if (
    i.name === 'FunctionsFetchError' ||
    i.name === 'AuthRetryableFetchError' ||
    has('failed to fetch', 'failed to send a request', 'networkerror', 'network request failed', 'load failed', 'fetch failed', 'internet connection appears to be offline')
  ) {
    return make('network', 'Geen verbinding met Antreja.', 'Controleer je internet (wifi of mobiele data) en probeer het opnieuw.', true, i, 'network');
  }
  if (i.name === 'FunctionsRelayError' || has('relay error')) {
    return make('server', 'De server is even niet bereikbaar.', 'Probeer het over een minuut opnieuw. Blijft het misgaan? Stuur de technische details naar support.', true, i, 'relay');
  }

  // --- Sessie / rechten ---
  if (
    code === 'pgrst301' ||
    code === 'session_not_found' ||
    code === 'refresh_token_not_found' ||
    code === 'bad_jwt' ||
    i.status === 401 ||
    has('jwt expired', 'invalid jwt', 'refresh token', 'auth session missing') ||
    (i.bodyError ?? '').toLowerCase() === 'unauthorized'
  ) {
    return make('session', 'Je bent uitgelogd.', 'Log opnieuw in en probeer het daarna nog eens.', false, i, code || 'session');
  }
  if (code === '42501' || i.status === 403 || has('permission denied', 'row-level security', 'forbidden', 'geen rechten', 'insufficient_aal')) {
    return make('forbidden', 'Je hebt hier geen rechten voor.', 'Vraag je clubbeheerder om je de juiste rol te geven.', false, i, code || 'forbidden');
  }
  if (has('profiel ontbreekt') || (i.message.toLowerCase().includes('profiel') && i.status === 404)) {
    return make('not_invited', 'Je account is nog niet aan een club gekoppeld.', 'Vraag je clubbeheerder om een uitnodiging.', false, i, 'no_profile');
  }
  if (code === '23505' || has('duplicate key', 'already exists', 'already registered')) {
    return make('conflict', 'Dit bestaat al.', 'Kies een andere naam of open het bestaande item.', false, i, '23505');
  }
  if (code === '23503' || code === '23514' || code === '22p02' || code.startsWith('pgrst1') || i.status === 400 || i.status === 422) {
    return make('invalid_input', 'Niet alle gegevens zijn goed ingevuld.', 'Controleer de velden en probeer het opnieuw.', false, i);
  }
  if (i.status === 404 || code === 'pgrst116' || has('not found', 'niet gevonden')) {
    return make('not_found', 'Dit item bestaat niet (meer).', 'Ververs de pagina. Misschien heeft iemand anders het net verwijderd.', false, i);
  }
  if (has('server niet geconfigureerd')) {
    return make('server', 'De server is nog niet goed ingesteld.', 'Dit kun je niet zelf oplossen. Stuur de technische details naar support.', false, i, 'server_config');
  }
  if (has('slotaccount kon niet', 'tuya-account kon niet', 'provider')) {
    return make('server', 'Het slotplatform reageert nu niet.', 'Probeer het over een paar minuten opnieuw.', true, i, 'provider');
  }
  if ((i.status !== undefined && i.status >= 500) || i.name === 'FunctionsHttpError' || has('non-2xx status code')) {
    return make('server', 'De server kon dit nu niet afhandelen.', 'Probeer het opnieuw. Blijft het misgaan? Stuur de technische details naar support.', true, i);
  }
  return null;
}

export const UNKNOWN_ERROR = {
  title: 'Er ging iets mis.',
  hint: 'Probeer het opnieuw. Blijft het misgaan? Stuur de technische details naar support.',
};

/** Hoofdingang: altijd een bruikbare Nederlandse melding. */
export function describeError(err: unknown, opts: { source?: string; fallbackTitle?: string } = {}): FriendlyError {
  const i = errorInfoSync(err, opts.source);
  return (
    mapCommonError(i) ?? {
      kind: 'unknown',
      title: opts.fallbackTitle ?? UNKNOWN_ERROR.title,
      hint: UNKNOWN_ERROR.hint,
      retryable: true,
      details: formatDetails(i),
      code: i.code,
    }
  );
}

/** Eén regel, voor plekken zonder ruimte voor een volledige foutkaart (toasts, veldfouten). */
export function errorLine(err: unknown, opts?: { source?: string; fallbackTitle?: string }): string {
  const f = describeError(err, opts);
  return `${f.title} ${f.hint}`;
}
